"use client";

import React, { forwardRef, type CSSProperties } from "react";
import { cn } from "../../lib/utilsComprehensive";
import { LiquidGlassEffectGroup } from "../../primitives/LiquidGlassEffectGroup";
import { LiquidGlassMaterial } from "../../primitives/LiquidGlassMaterial";

export interface LiquidGlassBadgeClusterItem {
  id: string;
  label: string;
  selected?: boolean;
  disabled?: boolean;
}

export interface LiquidGlassBadgeClusterProps
  extends React.HTMLAttributes<HTMLDivElement> {
  items: LiquidGlassBadgeClusterItem[];
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  maxCollapsed?: number;
  selectionMode?: "none" | "single" | "multiple";
  materialVariant?: "regular" | "clear";
}

const badgeClusterButtonStyle: CSSProperties = {
  border: 0,
  background: "transparent",
  color: "inherit",
  cursor: "pointer",
  font: "inherit",
};

export const LiquidGlassBadgeCluster = forwardRef<
  HTMLDivElement,
  LiquidGlassBadgeClusterProps
>(
  (
    {
      items,
      expanded = false,
      onExpandedChange,
      maxCollapsed = 3,
      selectionMode = "none",
      materialVariant = "regular",
      className,
      ...props
    },
    ref
  ) => {
    const visible = expanded ? items : items.slice(0, maxCollapsed);
    const hiddenCount = Math.max(items.length - visible.length, 0);
    return (
      <LiquidGlassMaterial
        ref={ref}
        material="standard"
        variant={materialVariant}
        intent="neutral"
        sheen={0}
        tintMode="light"
        adaptToContent={false}
        enableRefraction={false}
        enableReflection={false}
        enableParallax={false}
        radius="full"
        className={cn(
          "liquid-glass-badge-cluster glass-inline-flex",
          className
        )}
        data-selection-mode={selectionMode}
        style={
          {
            "--glass-theme-text": "rgba(15, 23, 42, 0.92)",
            color: "var(--glass-theme-text, var(--glass-text-primary))",
          } as React.CSSProperties
        }
        {...props}
      >
        <LiquidGlassEffectGroup className="glass-flex glass-items-center glass-gap-1 glass-p-1">
          {visible.map((item) => (
            <span
              key={item.id}
              aria-pressed={item.selected}
              className={cn(
                "glass-radius-full glass-border glass-border-black/10 glass-px-2 glass-py-1 glass-text-xs glass-font-medium",
                item.selected && "glass-border-black/25"
              )}
              style={{
                background: item.selected
                  ? "rgba(255, 255, 255, 0.32)"
                  : "rgba(255, 255, 255, 0.14)",
                color: "var(--glass-theme-text, var(--glass-text-primary))",
              }}
            >
              {item.label}
            </span>
          ))}
          {hiddenCount > 0 && (
            <button
              type="button"
              className="glass-radius-full glass-px-2 glass-py-1 glass-text-xs"
              style={badgeClusterButtonStyle}
              onClick={() => onExpandedChange?.(!expanded)}
            >
              +{hiddenCount}
            </button>
          )}
        </LiquidGlassEffectGroup>
      </LiquidGlassMaterial>
    );
  }
);

LiquidGlassBadgeCluster.displayName = "LiquidGlassBadgeCluster";
